import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import type { FloristProfile } from './FeaturedFlorists';

interface FeaturedFloristsSectionProps {
  florists?: FloristProfile[];
}

export function FeaturedFloristsSection({ florists = [] }: FeaturedFloristsSectionProps) {
  // Ensure we always show 8 cards
  const displayFlorists = [...florists];
  const placeholderNames = ['Bondi Blooms', 'Newtown Flowers', 'Paddington Petals', 'Surry Hills Stems', 'Manly Bouquets', 'Darlinghurst Florals', 'Woollahra Blooms', 'Potts Point Petals'];
  const placeholderLocations = ['Bondi, NSW', 'Newtown, NSW', 'Paddington, NSW', 'Surry Hills, NSW', 'Manly, NSW', 'Darlinghurst, NSW', 'Woollahra, NSW', 'Potts Point, NSW'];
  
  while (displayFlorists.length < 8) {
    const index = displayFlorists.length;
    displayFlorists.push({
      id: `placeholder-${index}`,
      store_name: placeholderNames[index],
      suburb: placeholderLocations[index].split(', ')[0],
      state: 'NSW',
      storefront_image_url: '/images/roses-hero.jpg',
      about_text: '',
      banner_url: '',
      cover_image_url: '',
      delivery_settings: {}
    });
  }

  return (
    <section className="w-full py-20 bg-neutral-50">
      <div className="max-w-[1440px] mx-auto px-6">
        <div className="text-left mb-12">
          <h2 className="text-4xl md:text-5xl font-semibold text-brand-secondary tracking-tight mb-3 satoshi-font">
            Featured Florists
          </h2>
          <p className="text-neutral-600 text-[15px] max-w-2xl satoshi-font">
            Trusted local florists delivering beautiful arrangements across Sydney
          </p>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {displayFlorists.slice(0, 8).map((florist) => (
            <Link 
              key={florist.id} 
              to={florist.id.startsWith('placeholder') ? '#' : `/florist/${florist.id}`} 
              className="block group h-full"
            >
              <div className="bg-white rounded-lg overflow-hidden border border-neutral-200 hover:border-neutral-300 hover:scale-[1.01] transition-all duration-200 h-full flex flex-col">
                <div className="aspect-square w-full overflow-hidden flex-shrink-0 bg-neutral-100">
                  <img 
                    src={florist.storefront_image_url || florist.cover_image_url || florist.banner_url || '/images/placeholder-store.jpg'} 
                    alt={florist.store_name} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="eager"
                    decoding="async"
                    style={{
                      imageRendering: '-webkit-optimize-contrast',
                      WebkitBackfaceVisibility: 'hidden',
                      transform: 'translateZ(0)'
                    }}
                  />
                </div>
                <div className="p-4 text-center">
                  <h3 className="font-semibold text-base text-brand-secondary mb-1 line-clamp-2 satoshi-font">
                    {florist.store_name}
                  </h3>
                  <p className="text-sm text-neutral-600 satoshi-font">
                    {florist.suburb}{florist.suburb && florist.state ? ", " : ""}{florist.state}
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>
        
        {/* Show More CTA */}
        <div className="text-center mt-12">
          <Button 
            variant="outline" 
            className="border-2 border-brand-primary text-brand-primary bg-[#967F81] hover:bg-[#843329] hover:text-white px-8 py-3 text-base font-semibold rounded-full transition-all duration-200 satoshi-font uppercase tracking-wide"
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
              setTimeout(() => {
                const searchInput = document.querySelector('input[placeholder*="suburb"]');
                if (searchInput) {
                  (searchInput as HTMLInputElement).focus();
                }
              }, 500);
            }}
          >
            Browse All Florists
          </Button>
        </div>
      </div>
    </section>
  );
}
